import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import { PaginateQuery } from "../../interfaces";
import {
  AddGradeToStageRequest,
  CreateEducationalStageRequest,
  EducationalStageDto,
  UpdateGradeInStageRequest,
} from "../../interfaces/curriculum";
import { SelectListDto } from "../../models";

export type UpdateEducationalStageRequest =
  Partial<CreateEducationalStageRequest>;

export type EducationalStageSelectListDto = SelectListDto & {
  courseId?: string;
};

export const educationalStageApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEducationalStages: builder.query<
      { data: EducationalStageDto[]; meta: any },
      PaginateQuery
    >({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/educational-stage"),
      }),
      providesTags: ["Paginated-EducationalStages"],
    }),
    getEducationalStageSelectList: builder.query<
      EducationalStageSelectListDto[],
      { curriculumId?: string; courseId?: string } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.curriculumId) {
          search.set("curriculumId", params.curriculumId);
        }
        if (params?.courseId) {
          search.set("courseId", params.courseId);
        }
        const qs = search.toString();
        return {
          url: qs
            ? `/api/educational-stage/select-list/all?${qs}`
            : `/api/educational-stage/select-list/all`,
        };
      },
      providesTags: ["EducationalStage"],
    }),
    getEducationalStage: builder.query<EducationalStageDto, string>({
      query: (id) => ({ url: `/api/educational-stage/${id}` }),
      providesTags: ["EducationalStage"],
    }),
    addEducationalStage: builder.mutation<any, CreateEducationalStageRequest>({
      query: (data) => ({
        url: "/api/educational-stage",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["EducationalStage", "Paginated-EducationalStages"],
    }),
    updateEducationalStage: builder.mutation<
      any,
      { id: string; data: UpdateEducationalStageRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/educational-stage/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["EducationalStage", "Paginated-EducationalStages"],
    }),
    deleteEducationalStage: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/educational-stage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Paginated-EducationalStages"],
    }),
    addGradeToEducationalStage: builder.mutation<
      EducationalStageDto,
      { stageId: string; data: AddGradeToStageRequest }
    >({
      query: ({ stageId, data }) => ({
        url: `/api/educational-stage/${stageId}/grades`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "EducationalStage",
        "Paginated-EducationalStages",
        "CourseDurations",
        "Grades",
      ],
    }),
    updateGradeInEducationalStage: builder.mutation<
      EducationalStageDto,
      { stageId: string; gradeId: string; data: UpdateGradeInStageRequest }
    >({
      query: ({ stageId, gradeId, data }) => ({
        url: `/api/educational-stage/${stageId}/grades/${gradeId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "EducationalStage",
        "Paginated-EducationalStages",
        "CourseDurations",
        "Grades",
      ],
    }),
    removeGradeFromEducationalStage: builder.mutation<
      any,
      { stageId: string; gradeId: string }
    >({
      query: ({ stageId, gradeId }) => ({
        url: `/api/educational-stage/${stageId}/grades/${gradeId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "EducationalStage",
        "Paginated-EducationalStages",
        "CourseDurations",
        "Grades",
      ],
    }),
  }),
});

export const {
  useGetEducationalStagesQuery,
  useGetEducationalStageSelectListQuery,
  useGetEducationalStageQuery,
  useAddEducationalStageMutation,
  useUpdateEducationalStageMutation,
  useDeleteEducationalStageMutation,
  useAddGradeToEducationalStageMutation,
  useUpdateGradeInEducationalStageMutation,
  useRemoveGradeFromEducationalStageMutation,
} = educationalStageApi;
